import type { DirectedCastEntry } from './directedVideoApi';

/**
 * The names of the cast of a directed video (owner report 2026-10-18: the story text showed raw cast
 * ids such as "c-b39fdf9d"). Each entry gets one display name:
 * - its label, when the artist gave one;
 * - the artist name for the label "me" (the artist);
 * - else "Character N", N its place in the cast (1 for the first).
 *
 * The stored texts may hold cast ids (the director wrote them, or an old video). The screens put the
 * name in place of each id when they render (`withDirectedCastNames`), so a renamed entry shows its
 * new name in every old text too. The file has no heavy imports.
 */

/** The label that marks the artist. */
const ARTIST_LABEL = 'me';

/** The default name of the entry at `index` (0-based). The screens translate their own copy. */
export function defaultDirectedCastName(index: number): string {
  return `Character ${index + 1}`;
}

/** True when the label of the entry marks the artist ("me"). Pure. */
export function isDirectedArtistLabel(label: string): boolean {
  return label.trim().toLowerCase() === ARTIST_LABEL;
}

/**
 * The display name of each cast entry, by id. `artistName` names the label "me"; `fallback` makes
 * the name of an entry with no label (default "Character N"). Pure.
 */
export function directedCastNames(
  cast: readonly Pick<DirectedCastEntry, 'id' | 'label'>[],
  artistName: string | null,
  fallback: (index: number) => string = defaultDirectedCastName
): Map<string, string> {
  const names = new Map<string, string>();
  cast.forEach((entry, index) => {
    const label = entry.label.trim();
    const artist = artistName?.trim() ?? '';
    if (label !== '' && isDirectedArtistLabel(label) && artist !== '') {
      names.set(entry.id, artist);
    } else if (label !== '') {
      names.set(entry.id, label);
    } else {
      names.set(entry.id, fallback(index));
    }
  });
  return names;
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * A pattern that finds each token as a whole word: group 1 is the character before it (or the
 * start), group 2 the token. No lookbehind: older Safari versions refuse it.
 */
function tokenPattern(tokens: readonly string[], edge: string, flags: string): RegExp | null {
  const sorted = tokens.filter((token) => token !== '').sort((a, b) => b.length - a.length);
  if (sorted.length === 0) {
    return null;
  }
  return new RegExp(`(^|[^${edge}])(${sorted.map(escapeRegExp).join('|')})(?![${edge}])`, flags);
}

/** The characters that continue a cast id ("c-b39fdf9d"). */
const ID_EDGE = '\\w-';
/** The characters that continue a name. */
const NAME_EDGE = '\\p{L}\\p{N}_-';

/** The text with each cast id in it replaced by the name of that entry. Pure. */
export function withDirectedCastNames(text: string, names: ReadonlyMap<string, string>): string {
  const pattern = tokenPattern([...names.keys()], ID_EDGE, 'g');
  if (pattern === null || text === '') {
    return text;
  }
  return text.replace(pattern, (_match, before: string, id: string) => `${before}${names.get(id) ?? id}`);
}

/**
 * The text of an editor with each shown name put back to its cast id, so the stored text keeps the
 * id and a later rename shows the new name (the reverse of `withDirectedCastNames`). Only the names
 * that the editor showed (`names`) go back. A name that two entries share stays as it is. Pure.
 */
export function withDirectedCastIds(text: string, names: ReadonlyMap<string, string>): string {
  const idsByName = new Map<string, string[]>();
  for (const [id, name] of names) {
    idsByName.set(name, [...(idsByName.get(name) ?? []), id]);
  }
  const unique = [...idsByName.entries()].flatMap(([name, ids]) => (ids.length === 1 && name !== '' ? [name] : []));
  if (unique.length === 0 || text === '') {
    return text;
  }
  const pattern = tokenPattern(unique, NAME_EDGE, 'gu');
  if (pattern === null) {
    return text;
  }
  return text.replace(
    pattern,
    (_match, before: string, name: string) => `${before}${idsByName.get(name)?.[0] ?? name}`
  );
}
